import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentAccount, useSignAndExecuteTransaction } from '@mysten/dapp-kit';
import { ConnectButton } from '@mysten/dapp-kit';
import { useGovernanceConfig, useAGUBalance } from '../sui/hooks';
import { buildCreateProposalTx, buildVoteTx, buildFinalizeProposalTx } from '../sui/transactions';
import { explorerUrl } from '../sui/constants';

type Proposal = {
  id: string;
  title: string;
  description: string;
  proposer?: string;
  votesFor: number;
  votesAgainst: number;
  status: 'active' | 'passed' | 'rejected' | 'executed';
  createdAt?: string;
};

const defaultProposals: Proposal[] = [
  { id: '0', title: 'AGU Buyback & Burn 확대', description: 'Treasury의 40%를 Buyback & Burn에 배정', votesFor: 1240, votesAgainst: 380, status: 'active', createdAt: '2026-02-10' },
  { id: '1', title: 'Accuracy Decay 기준 상향', description: '비활성 기간에 대한 감쇠 속도를 조정', votesFor: 890, votesAgainst: 120, status: 'active', createdAt: '2026-02-05' },
  { id: '2', title: 'Slashing 최대율 60%로 하향', description: '초기 참여자 보호를 위해 최대 슬래싱을 80→60%로', votesFor: 2100, votesAgainst: 780, status: 'passed', createdAt: '2026-01-28' },
];

export default function Governance() {
  const { t } = useTranslation();
  const account = useCurrentAccount();
  const walletAddress = account?.address ?? null;
  const { config, isLoading: configLoading } = useGovernanceConfig();
  const { balance } = useAGUBalance();
  const { mutate: signAndExecute } = useSignAndExecuteTransaction();

  const [proposals] = useState<Proposal[]>(defaultProposals);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [lastTxDigest, setLastTxDigest] = useState<string | null>(null);

  const votingPeriodDays = config.votingPeriodMs > 0 ? Math.round(config.votingPeriodMs / 86400000) : 7;
  const timelockHours = config.timelockMs > 0 ? Math.round(config.timelockMs / 3600000) : 48;
  const quorumPct = config.quorumBps > 0 ? config.quorumBps / 100 : 25;

  const handleVote = (proposalId: string, voteFor: boolean) => {
    if (!walletAddress) return;
    setLoading(true);
    setActionMsg(null);

    try {
      const tx = buildVoteTx(proposalId, voteFor);
      signAndExecute(
        { transaction: tx },
        {
          onSuccess: (result) => {
            setActionMsg({ type: 'success', text: `Vote "${voteFor ? 'For' : 'Against'}" submitted!` });
            setLastTxDigest(result.digest);
            setLoading(false);
          },
          onError: (err) => {
            setActionMsg({ type: 'error', text: err.message || 'Vote failed' });
            setLoading(false);
          },
        }
      );
    } catch (e: any) {
      setActionMsg({ type: 'error', text: e.message || 'Failed to build transaction' });
      setLoading(false);
    }
  };

  const handleCreate = () => {
    if (!walletAddress || !newTitle) return;
    setLoading(true);
    setActionMsg(null);

    try {
      const tx = buildCreateProposalTx(newTitle, newDesc);
      signAndExecute(
        { transaction: tx },
        {
          onSuccess: (result) => {
            setActionMsg({ type: 'success', text: `Proposal "${newTitle}" created on-chain!` });
            setLastTxDigest(result.digest);
            setNewTitle('');
            setNewDesc('');
            setShowCreate(false);
            setLoading(false);
          },
          onError: (err) => {
            setActionMsg({ type: 'error', text: err.message || 'Failed to create proposal' });
            setLoading(false);
          },
        }
      );
    } catch (e: any) {
      setActionMsg({ type: 'error', text: e.message || 'Failed to build transaction' });
      setLoading(false);
    }
  };

  const handleFinalize = (proposalId: string) => {
    if (!walletAddress) return;
    setLoading(true);
    setActionMsg(null);

    try {
      const tx = buildFinalizeProposalTx(proposalId);
      signAndExecute(
        { transaction: tx },
        {
          onSuccess: (result) => {
            setActionMsg({ type: 'success', text: `Proposal #${proposalId} finalized!` });
            setLastTxDigest(result.digest);
            setLoading(false);
          },
          onError: (err) => {
            setActionMsg({ type: 'error', text: err.message || 'Finalization failed' });
            setLoading(false);
          },
        }
      );
    } catch (e: any) {
      setActionMsg({ type: 'error', text: e.message || 'Failed to build transaction' });
      setLoading(false);
    }
  };

  return (
    <div className="agu-container">
      <div className="agu-page-header">
        <h1>{t('nav.governance')}</h1>
        <p>AGU 거버넌스는 정확도 기반 영향력을 통해 의사결정을 수행합니다. 모든 투표는 SUI 온체인 트랜잭션입니다.</p>
      </div>

      <div className="agu-kpi-grid">
        <div className="agu-kpi-card">
          <span>Active Proposals</span>
          <strong>{configLoading ? '...' : config.proposalCount || proposals.length}</strong>
        </div>
        <div className="agu-kpi-card">
          <span>Voting Period</span>
          <strong>{votingPeriodDays} days</strong>
        </div>
        <div className="agu-kpi-card">
          <span>Quorum Required</span>
          <strong>{quorumPct}%</strong>
        </div>
        <div className="agu-kpi-card">
          <span>Timelock</span>
          <strong>{timelockHours}h</strong>
        </div>
      </div>

      <section className="agu-section">
        <div className="agu-grid">
          <div className="agu-card">
            <div className="agu-card-title">🗳️ On-chain Proposal Flow</div>
            <ul className="agu-list">
              <li>Create Proposal → SUI TX</li>
              <li>Vote (For/Against) → SUI TX</li>
              <li>Voting Period: {votingPeriodDays} days</li>
              <li>Timelock: {timelockHours}h after quorum met</li>
              <li>Finalize → Execute</li>
            </ul>
          </div>
          <div className="agu-card">
            <div className="agu-card-title">⚖️ Voting Power</div>
            <p className="agu-body">Voting Power는 보유량이 아니라 정확도 이력으로 가중됩니다.</p>
            <div className="agu-highlight">Voting Power = Staked AGU × (1 + Accuracy Score)</div>
            {walletAddress && (
              <p className="agu-muted" style={{ marginTop: 8 }}>Your AGU: {balance.toLocaleString()}</p>
            )}
          </div>
        </div>
      </section>

      <section className="agu-section">
        <div className="agu-panel">
          <div className="agu-panel-header">
            <h2>Active Proposals</h2>
            <p>현재 진행 중인 거버넌스 제안 목록입니다. 투표는 SUI 트랜잭션으로 기록됩니다.</p>
          </div>

          {actionMsg && (
            <div className={`agu-alert ${actionMsg.type}`}>
              {actionMsg.text}
              {lastTxDigest && (
                <> — <a href={explorerUrl('tx', lastTxDigest)} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>View TX ↗</a></>
              )}
            </div>
          )}

          <div className="agu-proposal-list">
            {proposals.map((proposal) => (
              <div className="agu-proposal-card" key={proposal.id}>
                <div style={{ flex: 1 }}>
                  <h3>{proposal.title}</h3>
                  <p className="agu-muted">{proposal.description}</p>
                  <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 13 }}>
                    <span style={{ color: '#10b981' }}>👍 {proposal.votesFor}</span>
                    <span style={{ color: '#ef4444' }}>👎 {proposal.votesAgainst}</span>
                    <span className="agu-status-pill">{proposal.status}</span>
                    {proposal.createdAt && (
                      <span className="agu-meta">{new Date(proposal.createdAt).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                <div className="agu-proposal-actions">
                  {walletAddress && proposal.status === 'active' ? (
                    <>
                      <button className="agu-vote-btn for" onClick={() => handleVote(proposal.id, true)} disabled={loading}>
                        👍 For
                      </button>
                      <button className="agu-vote-btn against" onClick={() => handleVote(proposal.id, false)} disabled={loading}>
                        👎 Against
                      </button>
                    </>
                  ) : walletAddress && proposal.status === 'passed' ? (
                    <button className="agu-button" onClick={() => handleFinalize(proposal.id)} disabled={loading} style={{ fontSize: 12, padding: '6px 12px' }}>
                      ⚡ Finalize
                    </button>
                  ) : !walletAddress ? (
                    <span className="agu-status-pill">Connect wallet to vote</span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>

          <div className="agu-form-actions" style={{ marginTop: '20px' }}>
            {!walletAddress ? (
              <ConnectButton />
            ) : !showCreate ? (
              <button className="agu-button" onClick={() => setShowCreate(true)}>
                ✏️ Create Proposal (On-chain)
              </button>
            ) : (
              <div className="agu-form" style={{ width: '100%' }}>
                <label htmlFor="proposalTitle">Proposal Title</label>
                <input
                  id="proposalTitle"
                  className="agu-input"
                  placeholder="Enter proposal title"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                />
                <label htmlFor="proposalDesc">Description</label>
                <textarea
                  id="proposalDesc"
                  className="agu-input"
                  placeholder="Describe your proposal"
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  rows={3}
                  style={{ resize: 'vertical', minHeight: '80px' }}
                />
                <div className="agu-form-actions">
                  <button className="agu-button" onClick={handleCreate} disabled={loading || !newTitle}>
                    {loading ? '⏳ Submitting...' : '📤 Submit to SUI'}
                  </button>
                  <button className="agu-button secondary" onClick={() => setShowCreate(false)}>
                    Cancel
                  </button>
                </div>
                <p className="agu-muted" style={{ fontSize: 12, marginTop: 8 }}>
                  This will create an on-chain governance proposal. You will need to sign a SUI transaction.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="agu-section">
        <div className="agu-panel">
          <div className="agu-panel-header">
            <h2>Governance Contract Info</h2>
          </div>
          <div className="agu-table-wrapper">
            <table className="agu-table">
              <tbody>
                <tr>
                  <td style={{ color: 'var(--agu-muted)' }}>Network</td>
                  <td>SUI Testnet</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--agu-muted)' }}>GovernanceConfig</td>
                  <td>
                    <a href={explorerUrl('object', '0xf5bdb60f1525b3a9e6ae29fc02583f34bca8d103f0d6a6c5a3dbc3fadf369467')} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--agu-accent)' }}>
                      0xf5bd...9467 ↗
                    </a>
                  </td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--agu-muted)' }}>Voting Period</td>
                  <td>{votingPeriodDays} days</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--agu-muted)' }}>Timelock</td>
                  <td>{timelockHours} hours</td>
                </tr>
                <tr>
                  <td style={{ color: 'var(--agu-muted)' }}>Quorum</td>
                  <td>{quorumPct}%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
